import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Joi from '@hapi/joi';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ROLES } from 'appConstants';
import CustomTextField from 'components/Forms/TextField';
import CustomSelect from 'components/Forms/CustomSelect';
import CustomDatePicker from 'components/Forms/CustomDatePicker';
import SubmitErrorMessage from 'components/Forms/SubmitErrorMessage';
import LanguageMatrixField, { emptyLanguageSelection } from 'pages/Templates/Form/LanguageMatrixField';
import { mapExpirationMonth } from 'pages/TrainingModules/helpers/expirationMonth';
import { buildMoodleLanguageLinksPayload } from './helpers/buildMoodleLanguageLinksPayload';
import { parseTrainingPartVersionCode } from './helpers/parseTrainingPartVersionCode';
import {
  formatTrainingPartVersionCode,
  resolveCreateTrainingPartDefaults,
  resolveMinComplianceFromDate,
  validateTrainingPartAgainstPrevious,
} from './helpers/trainingPartPreviousConstraints';
import { resolveApiErrorMessage, isEditSaveDisabled } from 'utils';
import { canLaunchEntity, isDraftStatus } from 'utils/entityLifecycleStatus';
import { parseApiCalendarDate, toApiCalendarDate } from 'utils/calendarDate';

const PART_STATUSES = ['draft', 'scheduled', 'active', 'retired', 'cancelled'];
const TRANSITION_POLICIES = ['accept_existing_until_expiry', 'require_immediate_recertification'];

const useStyles = makeStyles(theme => ({
  actions: {
    marginTop: 24,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  lifecycleActions: {
    display: 'flex',
    alignItems: 'center',
  },
  formActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginLeft: 'auto',
  },
  moodleSection: {
    marginTop: theme.spacing(3),
  },
}));

const resolveValidityMonthValue = value => {
  if (value && typeof value === 'object' && value.value !== undefined) {
    return Number(value.value);
  }
  return Number(value ?? 0);
};

const isEmptyValidationValue = value => value == null || value === '';

const translateValidationKey = (key, options) => i18next.t(key, options);

const buildValidate = previousTrainingPart => values => {
  const schema = Joi.object({
    code: Joi.string().trim().min(1).max(64).required(),
    version: Joi.number().integer().min(2000).max(3000).required(),
    revision: Joi.number().integer().min(1).max(99).required(),
    validityMonthCount: Joi.object({
      value: Joi.number().integer().min(0).required(),
      label: Joi.string().required(),
    }).required(),
    availableFrom: Joi.date().required(),
    availableTo: Joi.date().allow(null, '').optional(),
    complianceFrom: Joi.date().required(),
    complianceTo: Joi.date().allow(null, '').optional(),
    transitionPolicy: Joi.object({
      value: Joi.string()
        .valid(...TRANSITION_POLICIES)
        .required(),
      label: Joi.string().required(),
    }).required(),
    status: Joi.object({
      value: Joi.string()
        .valid(...PART_STATUSES)
        .required(),
      label: Joi.string().required(),
    }).optional(),
  }).unknown(true);

  const { error } = schema.validate(values, { abortEarly: false });

  if (!error) {
    if (
      values.availableTo &&
      values.availableFrom &&
      new Date(values.availableTo).getTime() < new Date(values.availableFrom).getTime()
    ) {
      return { availableTo: translateValidationKey('trainingParts.availableToBeforeFrom') };
    }

    if (
      values.complianceTo &&
      values.complianceFrom &&
      new Date(values.complianceTo).getTime() < new Date(values.complianceFrom).getTime()
    ) {
      return { complianceTo: translateValidationKey('trainingParts.complianceToBeforeFrom') };
    }

    const previousErrors = validateTrainingPartAgainstPrevious(values, previousTrainingPart);
    return Object.keys(previousErrors).reduce((acc, field) => {
      acc[field] = translateValidationKey(previousErrors[field]);
      return acc;
    }, {});
  }

  return error.details.reduce((errors, detail) => {
    const field = ['status', 'transitionPolicy', 'validityMonthCount'].includes(detail.path[0])
      ? detail.path[0]
      : detail.path.join('.');
    const message = String(detail.message || '');

    if (message.startsWith('trainingParts.') || message.startsWith('trainingPart.') || message.startsWith('errors.')) {
      errors[field] = translateValidationKey(message);
      return errors;
    }

    const emptyValue = isEmptyValidationValue(detail.context?.value);
    if (
      detail.type === 'any.required' ||
      detail.type === 'string.empty' ||
      detail.type === 'object.base' ||
      detail.type === 'object.unknown' ||
      (detail.type === 'date.base' && emptyValue) ||
      (detail.type === 'number.base' && emptyValue)
    ) {
      errors[field] = translateValidationKey('errors.fieldIsRequired');
      return errors;
    }

    if (field === 'version' && (detail.type === 'number.base' || detail.type.startsWith('number.'))) {
      errors[field] = translateValidationKey('trainingParts.invalidVersionYear');
      return errors;
    }

    if (field === 'revision' && (detail.type === 'number.base' || detail.type.startsWith('number.'))) {
      errors[field] = translateValidationKey('trainingParts.invalidRevision');
      return errors;
    }

    if (detail.type === 'string.max') {
      errors[field] = translateValidationKey('errors.maxLength', { max: detail.context?.limit });
      return errors;
    }

    errors[field] = detail.type;
    return errors;
  }, {});
};

const TrainingPartForm = ({
  mode,
  layout,
  initialValues,
  previousTrainingPart = null,
  trainingMethod,
  trainingModuleCode,
  onSubmitForm,
  onLaunch,
  onCancelEntity,
  onCancel,
  backUrl,
  backState,
  submitting,
  isReadOnly = false,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation('common');
  const role = useSelector(state => state.auth.profile?.role);
  const isHyperAdmin = role === ROLES.HYPER;
  const isPageLayout = layout === 'page';
  const showPartMoodlePicker = trainingMethod === 'theory' && isHyperAdmin;
  const currentStatus = initialValues?.status || 'draft';
  const isDraft = isDraftStatus(currentStatus);
  const canEditFromDates = mode === 'create' || isDraft;
  const canEditAvailableTo =
    mode === 'create' || isDraft || Boolean(initialValues?.canEditAvailableTo);
  const canEditComplianceTo =
    mode === 'create' || isDraft || Boolean(initialValues?.canEditComplianceTo);
  const canCancelPart =
    initialValues?.canCancel !== undefined
      ? Boolean(initialValues.canCancel)
      : Boolean(initialValues?.status) &&
        !isDraft &&
        initialValues?.status !== 'cancelled' &&
        !initialValues?.hasNext;
  const showLaunch = mode === 'edit' && !isReadOnly && canLaunchEntity(currentStatus) && Boolean(onLaunch);
  const showCancelEntity = mode === 'edit' && !isReadOnly && canCancelPart && Boolean(onCancelEntity);
  const resolvedPreviousTrainingPart =
    mode === 'create' ? previousTrainingPart : initialValues?.previousTrainingPart || null;
  const previousTrainingPartVersionCode =
    formatTrainingPartVersionCode(resolvedPreviousTrainingPart) || resolvedPreviousTrainingPart?.versionCode || null;
  const minComplianceFromDate = resolveMinComplianceFromDate(resolvedPreviousTrainingPart);
  const validate = useMemo(
    () => buildValidate(mode === 'create' ? resolvedPreviousTrainingPart : null),
    [mode, resolvedPreviousTrainingPart],
  );

  const statusOptions = useMemo(
    () =>
      PART_STATUSES.map(value => ({
        value,
        label: t(`trainingParts.statuses.${value}`),
      })),
    [t],
  );

  const transitionPolicyOptions = useMemo(
    () =>
      TRANSITION_POLICIES.map(value => ({
        value,
        label: t(`trainingParts.transitionPolicies.${value}`),
      })),
    [t],
  );

  const formInitialValues = useMemo(() => {
    const defaultStatus = statusOptions.find(option => option.value === 'draft') || statusOptions[0];
    const defaultTransitionPolicy =
      transitionPolicyOptions.find(option => option.value === 'accept_existing_until_expiry') ||
      transitionPolicyOptions[0];

    const parsedVersion = parseTrainingPartVersionCode(initialValues?.versionCode);
    const createDefaults = mode === 'create' ? resolveCreateTrainingPartDefaults(previousTrainingPart) : null;
    const version =
      initialValues?.version ||
      createDefaults?.version ||
      parsedVersion.version ||
      (mode === 'create' ? String(new Date().getFullYear()) : '');
    const revision = initialValues?.revision ?? createDefaults?.revision ?? parsedVersion.revision ?? '1';
    const availableFrom = parseApiCalendarDate(initialValues?.availableFrom) || new Date();
    const defaultComplianceFrom =
      parseApiCalendarDate(initialValues?.complianceFrom) ||
      createDefaults?.complianceFrom ||
      parseApiCalendarDate(initialValues?.availableFrom) ||
      availableFrom;

    return {
      code: trainingModuleCode || initialValues?.code || '',
      version,
      revision,
      validityMonthCount: mapExpirationMonth(initialValues?.validityMonthCount ?? 12),
      availableFrom,
      availableTo: parseApiCalendarDate(initialValues?.availableTo),
      complianceFrom: defaultComplianceFrom,
      complianceTo:
        parseApiCalendarDate(initialValues?.complianceTo) || parseApiCalendarDate(initialValues?.availableTo),
      transitionPolicy:
        transitionPolicyOptions.find(option => option.value === initialValues?.transitionPolicy) ||
        defaultTransitionPolicy,
      status:
        statusOptions.find(option => option.value === initialValues?.status) ||
        (mode === 'create' ? statusOptions.find(option => option.value === 'draft') || defaultStatus : defaultStatus),
      moodleLanguageLinks: initialValues?.moodleLanguageLinks || emptyLanguageSelection,
    };
  }, [initialValues, mode, previousTrainingPart, statusOptions, trainingModuleCode, transitionPolicyOptions]);

  const handleSubmit = async values => {
    const { moodleLanguageLinks, ...restValues } = values;
    const payload = {
      code: restValues.code.trim(),
      version: String(restValues.version || '').trim(),
      revision: String(restValues.revision ?? '0').trim(),
      validityMonthCount: resolveValidityMonthValue(restValues.validityMonthCount),
      availableFrom: toApiCalendarDate(restValues.availableFrom),
      availableTo: toApiCalendarDate(restValues.availableTo),
      complianceFrom: toApiCalendarDate(restValues.complianceFrom),
      complianceTo: toApiCalendarDate(restValues.complianceTo),
      transitionPolicy: restValues.transitionPolicy?.value,
    };

    if (mode === 'create') {
      payload.trainingMethod = trainingMethod;
      payload.status = 'draft';
    }

    if (showPartMoodlePicker) {
      payload.moodleLanguageLinks = buildMoodleLanguageLinksPayload(
        restValues.code,
        restValues.version,
        restValues.revision,
        moodleLanguageLinks,
      );
    }

    const result = await onSubmitForm(payload);
    if (result?.error || result?.statusCode) {
      const formError = resolveApiErrorMessage(result.message, {
        t,
        fallbackMessage: t('errors.serverError'),
      });
      const messages = Array.isArray(result.message) ? result.message : [result.message];
      if (messages.includes('trainingPart.alreadyExists')) {
        return {
          version: t('trainingPart.alreadyExists'),
          revision: t('trainingPart.alreadyExists'),
          [FORM_ERROR]: formError,
        };
      }
      return {
        [FORM_ERROR]: formError,
      };
    }
    return undefined;
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    if (backUrl) {
      history.push(backUrl, backState || undefined);
    }
  };

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={formInitialValues}
      keepDirtyOnReinitialize
      validate={validate}
      render={({
        handleSubmit: submitForm,
        submitting: formSubmitting,
        pristine,
        submitError,
        submitFailed,
        values,
        errors,
      }) => {
        const isBusy = submitting || formSubmitting;
        const isSaveDisabled = isEditSaveDisabled({
          submitting: isBusy,
          pristine,
          isEditMode: mode === 'edit',
        });
        const validationErrorMessages = Object.values(errors || {}).filter(Boolean);
        const firstValidationErrorKey =
          submitFailed && !submitError
            ? validationErrorMessages.find(message => String(message).startsWith('trainingParts.')) ||
              validationErrorMessages.find(message => String(message).startsWith('trainingPart.')) ||
              validationErrorMessages.find(message => String(message).startsWith('errors.')) ||
              null
            : null;

        const currentValidityValue = values.validityMonthCount?.value;
        const validityMonthOptions = [
          { label: '12', value: 12 },
          { label: '24', value: 24 },
          { label: '36', value: 36 },
          ...(currentValidityValue === 0 ? [{ label: t('label.infinity'), value: 0 }] : []),
        ];

        return (
          <form onSubmit={submitForm}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Field
                  type="text"
                  name="code"
                  id="code"
                  label={t('trainingModules.code')}
                  component={CustomTextField}
                  variant="outlined"
                  fullWidth
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Field
                  type="text"
                  name="version"
                  id="version"
                  label={t('trainingParts.version')}
                  component={CustomTextField}
                  variant="outlined"
                  fullWidth
                  disabled={isBusy || isReadOnly || (mode === 'edit' && !isDraftStatus(currentStatus))}
                  placeholder="2026"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Field
                  type="text"
                  name="revision"
                  id="revision"
                  label={t('trainingParts.revision')}
                  component={CustomTextField}
                  variant="outlined"
                  fullWidth
                  disabled={isBusy || isReadOnly || (mode === 'edit' && !isDraftStatus(currentStatus))}
                  placeholder="1"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  {t('trainingParts.versionFormatHint')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  type="select"
                  name="validityMonthCount"
                  id="validityMonthCount"
                  label={t('formLabels.validityMonth')}
                  component={CustomSelect}
                  options={validityMonthOptions}
                  variant="outlined"
                  fullWidth
                  isDisabled={isBusy || isReadOnly}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  type="text"
                  name="availableFrom"
                  id="availableFrom"
                  label={t('tablesLabels.availableFrom')}
                  component={CustomDatePicker}
                  variant="outlined"
                  fullWidth
                  disabled={isBusy || isReadOnly || !canEditFromDates}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  type="text"
                  name="availableTo"
                  id="availableTo"
                  label={t('tablesLabels.availableTo')}
                  component={CustomDatePicker}
                  variant="outlined"
                  fullWidth
                  disabled={isBusy || isReadOnly || !canEditAvailableTo}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  type="text"
                  name="complianceFrom"
                  id="complianceFrom"
                  label={t('trainingParts.complianceFrom')}
                  component={CustomDatePicker}
                  variant="outlined"
                  fullWidth
                  disabled={isBusy || isReadOnly || !canEditFromDates}
                  minDate={minComplianceFromDate || undefined}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  type="text"
                  name="complianceTo"
                  id="complianceTo"
                  label={t('trainingParts.complianceTo')}
                  component={CustomDatePicker}
                  variant="outlined"
                  fullWidth
                  disabled={isBusy || isReadOnly || !canEditComplianceTo}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  type="select"
                  name="transitionPolicy"
                  id="transitionPolicy"
                  label={t('trainingParts.transitionPolicy')}
                  component={CustomSelect}
                  options={transitionPolicyOptions}
                  variant="outlined"
                  fullWidth
                  isDisabled={isBusy || isReadOnly}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  type="select"
                  name="status"
                  id="status"
                  label={t('tablesLabels.status')}
                  component={CustomSelect}
                  options={statusOptions}
                  variant="outlined"
                  fullWidth
                  isDisabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  input={{
                    value: previousTrainingPartVersionCode || '—',
                    name: 'previousTrainingPartDisplay',
                    type: 'text',
                  }}
                  meta={{}}
                  id="previousTrainingPart"
                  label={t('trainingParts.previousTrainingPart')}
                  variant="outlined"
                  fullWidth
                  disabled
                />
              </Grid>
            </Grid>

            {showPartMoodlePicker && (
              <Box className={classes.moodleSection}>
                <Field
                  name="moodleLanguageLinks"
                  component={LanguageMatrixField}
                  trainingPartCode={values.code || ''}
                  trainingPartId={initialValues?.id}
                  allowAutoSelect={mode === 'create'}
                  disabled={isBusy || isReadOnly}
                />
              </Box>
            )}

            {submitFailed && submitError && <SubmitErrorMessage message={submitError} />}
            {firstValidationErrorKey && (
              <SubmitErrorMessage
                message={resolveApiErrorMessage(firstValidationErrorKey, {
                  t,
                  fallbackMessage: t('errors.serverError'),
                })}
              />
            )}

            <Box className={classes.actions}>
              {(showLaunch || showCancelEntity) && (
                <Box className={classes.lifecycleActions}>
                  {showCancelEntity && (
                    <Button
                      variant="outlined"
                      disabled={isBusy}
                      onClick={async () => {
                        await onCancelEntity();
                      }}
                    >
                      {t('trainingParts.cancelEntity', 'Mark as cancelled')}
                    </Button>
                  )}
                  {showLaunch && (
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={isBusy}
                      onClick={async () => {
                        await onLaunch();
                      }}
                    >
                      {t('trainingParts.launch', 'Launch version')}
                    </Button>
                  )}
                </Box>
              )}
              <Box className={classes.formActions}>
                <Button variant="outlined" onClick={handleCancel} disabled={isBusy}>
                  {isPageLayout ? t('buttons.back') : t('buttons.cancel')}
                </Button>
                {!isReadOnly && (
                  <Button type="submit" color="primary" variant="contained" disabled={isSaveDisabled}>
                    {mode === 'edit' ? t('buttons.save') : t('trainingParts.actionAdd')}
                  </Button>
                )}
              </Box>
            </Box>
          </form>
        );
      }}
    />
  );
};

TrainingPartForm.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']),
  layout: PropTypes.oneOf(['modal', 'page']),
  initialValues: PropTypes.object,
  previousTrainingPart: PropTypes.object,
  trainingMethod: PropTypes.oneOf(['theory', 'ojt', 'practical']).isRequired,
  trainingModuleCode: PropTypes.string,
  onSubmitForm: PropTypes.func.isRequired,
  onLaunch: PropTypes.func,
  onCancelEntity: PropTypes.func,
  onCancel: PropTypes.func,
  backUrl: PropTypes.string,
  backState: PropTypes.object,
  submitting: PropTypes.bool,
  isReadOnly: PropTypes.bool,
};

TrainingPartForm.defaultProps = {
  mode: 'create',
  layout: 'modal',
  initialValues: null,
  previousTrainingPart: null,
  trainingModuleCode: '',
  onLaunch: null,
  onCancelEntity: null,
  onCancel: null,
  backUrl: null,
  backState: null,
  submitting: false,
  isReadOnly: false,
};

export default TrainingPartForm;
